import axios, {AxiosResponse} from 'axios'
import {Model as CourseScheduleModel} from './courseschedule/list/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const CUSTOM_URL = `${API_URL}/Custom`
const USER_SURVEY_APPLICATIONS_URL = `${CUSTOM_URL}/GetUserSurveyApplications`
const USER_SURVEY_INFO_URL = `${CUSTOM_URL}/GetUserSurveyInfo`
const STUDENT_POSTPONEMENT_URL = `${CUSTOM_URL}/AddStudentPostponement`
const STUDENT_POSTPONEMENT_INFO_URL = `${CUSTOM_URL}/GetStudentPostponementInfo`
const SURVEY_APPLICATION_DETAIL_URL = `${CUSTOM_URL}/GetSurveyApplicationDetail`
const UPDATE_SURVEY_APPLICATION_URL = `${CUSTOM_URL}/UpdateSurveyApplication`
const GRADUATE_PROFILE_URL = `${CUSTOM_URL}/GetGraduateProfile`
const SAVE_GRADUATE_PROFILE_URL = `${CUSTOM_URL}/SaveGraduateProfile`
const SUBMIT_GRADUATE_APPLICATION_URL = `${CUSTOM_URL}/SubmitGraduateApplication`
const UPLOAD_GRADUATE_CV_URL = `${CUSTOM_URL}/UploadGraduateCv`
const DOWNLOAD_GRADUATE_CV_URL = `${CUSTOM_URL}/DownloadGraduateCv`
const DOWNLOAD_GRADUATE_APPLICATIONS_EXCEL_URL = `${CUSTOM_URL}/DownloadGraduateApplicationsExcel`
const UPLOAD_GRADUATE_PHOTO_URL = `${CUSTOM_URL}/UploadGraduatePhoto`
const GRADUATE_APPLICATIONS_URL = `${CUSTOM_URL}/GetGraduateApplications`
const APPROVED_GRADUATES_URL = `${CUSTOM_URL}/GetApprovedGraduates`
const APPROVE_GRADUATE_APPLICATION_URL = `${CUSTOM_URL}/ApproveGraduateApplication`
const REJECT_GRADUATE_APPLICATION_URL = `${CUSTOM_URL}/RejectGraduateApplication`
const EXAM_RESULT_INPUT_URL = `${CUSTOM_URL}/GetExamResultInput`
const EXAM_RESULT_STATISTICS_URL = `${CUSTOM_URL}/GetExamResultStatistics`
const EXAM_RESULT_QUESTIONS_URL = `${CUSTOM_URL}/GetExamResultQuestions`
const EXAM_RESULT_STUDENTS_URL = `${CUSTOM_URL}/GetExamResultStudents`
const EXAM_RESULT_STUDENT_DETAILS_URL = `${CUSTOM_URL}/GetExamResultStudentDetails`
const EXAM_RESULT_STUDENT_BOOKLET_EVALUATION_URL = `${CUSTOM_URL}/EvaluateStudentBookletCodes`
const APPLY_STUDENT_BOOKLET_EVALUATION_URL = `${CUSTOM_URL}/ApplyStudentBookletEvaluation`
const UPDATE_EXAM_ANSWER_KEY_URL = `${CUSTOM_URL}/UpdateExamAnswerKey`
const SEND_EXAM_GRADES_TO_OBS_URL = `${CUSTOM_URL}/SendExamGradesToObs`
const SEARCH_APPLICATION_USERS_URL = `${CUSTOM_URL}/SearchApplicationUsers`

interface SlotDetail {
  timeSlotId: number
  detail?: string
}

export interface GraduateProfile {
  studentId?: number | null
  isGraduate: boolean
  graduateDate?: string | null
  gpa?: number | null
  hasGraduateRecord: boolean
  email?: string | null
  phone?: string | null
  isWorking?: boolean | null
  employerName?: string | null
  isJobSeeking?: boolean | null
  cvFileName?: string | null
  cvDownloadUrl?: string | null
  applicationStatus?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  rejectedAt?: string | null
  rejectionNote?: string | null
  cardCode?: string | null
  cardUrl?: string | null
  photoUrl?: string | null
}

export interface GraduateApplicationListItem {
  studentId: number
  fullName?: string | null
  studentNumber?: string | null
  organizationName?: string | null
  graduateDate?: string | null
  gpa?: number | null
  email?: string | null
  phone?: string | null
  applicationStatus?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  rejectedAt?: string | null
  isWorking?: boolean | null
  employerName?: string | null
  isJobSeeking?: boolean | null
  cvFileName?: string | null
  cardCode?: string | null
  rejectionNote?: string | null
}

export interface GraduateProfileUpdatePayload {
  email?: string | null
  phone?: string | null
  isWorking?: boolean | null
  employerName?: string | null
  isJobSeeking?: boolean | null
}

export interface SurveyApplicationDetail {
  id: number
  surveyId: number
  name?: string | null
  isCompleted: boolean
  surveyName?: string | null
  surveyGuid?: string | null
  surveyJson?: string | null
  courseOutcomeIsChecked: boolean
  organizationOutcomeIsChecked: boolean
  organizationEducationPurposeIsChecked: boolean
}

export interface UpdateSurveyApplicationPayload {
  id: number
  surveyId: number
  name: string
  isCompleted: boolean
}

export interface ExamAnswerKeyQuestionUpdatePayload {
  examQuestionId: number
  acceptedOptions: string[]
}

export interface ExamAnswerKeyBookletUpdatePayload {
  code?: string | null
  answers: ExamAnswerKeyQuestionUpdatePayload[]
}

export interface ExamPenaltyOptionsPayload {
  applyWrongPenalty?: boolean | null
  wrongCountForPenalty?: number | null
  correctCountToSubtract?: number | null
  cancelledQuestionIds?: number[] | null
  recoveredQuestionIds?: number[] | null
}

export interface ExamAnswerKeyUpdatePayload {
  examId: number
  booklets: ExamAnswerKeyBookletUpdatePayload[]
  penaltyOptions?: ExamPenaltyOptionsPayload | null
}

const createCustomCourseSchedule = (prop: CourseScheduleModel): Promise<any> => {
  return axios
    .put(`${CUSTOM_URL}/CreateCourseSchedule`, prop)
    .then((response: AxiosResponse<any>) => response.data)
}

const getCustomCourseSchedule = (
  semesterCourseId: number
): Promise<{
  theory: SlotDetail[]
  application1: SlotDetail[]
  application2: SlotDetail[]
  application3: SlotDetail[]
  laboratory: SlotDetail[]
}> => {
  return axios
    .get(`${CUSTOM_URL}/GetPlannedCourseSlots?semesterCourseId=${semesterCourseId}`)
    .then((response: AxiosResponse<any>) => response.data)
}

const deleteCustomCourseSchedule = (semesterCourseId: number, timeSlotId: number): Promise<any> => {
  return axios
    .delete(
      `${CUSTOM_URL}/DeleteCourseSchedule?semesterCourseId=${semesterCourseId}&timeSlotId=${timeSlotId}`
    )
    .then((response: AxiosResponse<any>) => response.data)
}

interface AssignInstructorPayload {
  semesterCourseId: number
  applicationUserId: number
  gradeLevel: number
}

const assignSemesterCourseInstructor = (payload: AssignInstructorPayload): Promise<any> => {
  return axios
    .post(`${CUSTOM_URL}/AssignSemesterCourseInstructor`, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

const fetchUserSurveyApplications = (): Promise<AxiosResponse<any>> => {
  return axios.get(`${USER_SURVEY_APPLICATIONS_URL}`)
}

const fetchUserSurveyInfo = (): Promise<AxiosResponse<any>> => {
  return axios.get(`${USER_SURVEY_INFO_URL}`)
}

const fetchStudentPostponementInfo = (): Promise<AxiosResponse<any>> => {
  return axios.get(`${STUDENT_POSTPONEMENT_INFO_URL}`)
}

const submitStudentPostponement = (): Promise<AxiosResponse<any>> => {
  return axios.put(`${STUDENT_POSTPONEMENT_URL}`)
}

const searchApplicationUsers = (search: string, take = 20): Promise<any> => {
  return axios
    .get(SEARCH_APPLICATION_USERS_URL, {params: {search, take}})
    .then((response: AxiosResponse<any>) => response.data)
}

const getSurveyApplicationDetail = (
  surveyApplicationId: number
): Promise<SurveyApplicationDetail> => {
  return axios
    .get(`${SURVEY_APPLICATION_DETAIL_URL}?surveyApplicationId=${surveyApplicationId}`)
    .then((response: AxiosResponse<SurveyApplicationDetail>) => response.data)
}

const submitSurveyApplication = (payload: UpdateSurveyApplicationPayload): Promise<any> => {
  return axios
    .post(`${UPDATE_SURVEY_APPLICATION_URL}`, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

const fetchGraduateProfile = (): Promise<AxiosResponse<GraduateProfile>> => {
  return axios.get(`${GRADUATE_PROFILE_URL}`)
}

const saveGraduateProfile = (payload: GraduateProfileUpdatePayload): Promise<any> => {
  return axios.post(`${SAVE_GRADUATE_PROFILE_URL}`, payload)
}

const submitGraduateApplication = (): Promise<any> => {
  return axios.post(`${SUBMIT_GRADUATE_APPLICATION_URL}`)
}

const uploadGraduateCv = (formData: FormData): Promise<AxiosResponse<any>> => {
  return axios.post(`${UPLOAD_GRADUATE_CV_URL}`, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
}

const downloadGraduateCv = (studentId?: number): Promise<AxiosResponse<Blob>> => {
  const query = studentId ? `?studentId=${studentId}` : ''
  return axios.get(`${DOWNLOAD_GRADUATE_CV_URL}${query}`, {responseType: 'blob'})
}

const downloadGraduateApplicationsExcel = (status?: string): Promise<AxiosResponse<Blob>> => {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return axios.get(`${DOWNLOAD_GRADUATE_APPLICATIONS_EXCEL_URL}${query}`, {responseType: 'blob'})
}

const uploadGraduatePhoto = (formData: FormData): Promise<AxiosResponse<any>> => {
  return axios.post(`${UPLOAD_GRADUATE_PHOTO_URL}`, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
}

const fetchGraduateApplications = (
  status?: string
): Promise<AxiosResponse<GraduateApplicationListItem[]>> => {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return axios.get(`${GRADUATE_APPLICATIONS_URL}${query}`)
}

const fetchApprovedGraduates = (): Promise<AxiosResponse<GraduateApplicationListItem[]>> => {
  return axios.get(`${APPROVED_GRADUATES_URL}`)
}

const approveGraduateApplication = (studentId: number): Promise<AxiosResponse<any>> => {
  return axios.post(`${APPROVE_GRADUATE_APPLICATION_URL}`, {studentId})
}

const rejectGraduateApplication = (payload: {
  studentId: number
  note?: string | null
}): Promise<AxiosResponse<any>> => {
  return axios.post(`${REJECT_GRADUATE_APPLICATION_URL}`, payload)
}

const getExamResultInput = (
  examId: number,
  options?: {
    pageNumber?: number
    pageSize?: number
    nameFilter?: string
    numberFilter?: string
    sortField?: string
    sortDirection?: string
  }
): Promise<any> => {
  const params: Record<string, any> = {examId}
  const config = typeof options === 'object' && options ? options : {}

  if (typeof config.pageNumber === 'number') {
    params.pageNumber = config.pageNumber
  }

  if (typeof config.pageSize === 'number') {
    params.pageSize = config.pageSize
  }

  if (typeof config.nameFilter === 'string' && config.nameFilter.trim().length > 0) {
    params.nameFilter = config.nameFilter.trim()
  }

  if (typeof config.numberFilter === 'string' && config.numberFilter.trim().length > 0) {
    params.numberFilter = config.numberFilter.trim()
  }

  if (typeof config.sortField === 'string' && config.sortField.trim().length > 0) {
    params.sortField = config.sortField.trim()
  }

  if (typeof config.sortDirection === 'string' && config.sortDirection.trim().length > 0) {
    params.sortDirection = config.sortDirection.trim()
  }

  return axios
    .get(EXAM_RESULT_INPUT_URL, {params})
    .then((response: AxiosResponse<any>) => response.data)
}

const getExamResultStatistics = (examId: number): Promise<any> => {
  return axios
    .get(EXAM_RESULT_STATISTICS_URL, {params: {examId}})
    .then((response: AxiosResponse<any>) => response.data)
}

const getExamResultQuestions = (examId: number): Promise<any> => {
  return axios
    .get(EXAM_RESULT_QUESTIONS_URL, {params: {examId}})
    .then((response: AxiosResponse<any>) => response.data)
}

const getExamResultStudents = (
  examId: number,
  options?: {
    pageNumber?: number
    pageSize?: number
    nameFilter?: string
    numberFilter?: string
    sortField?: string
    sortDirection?: string
  }
): Promise<any> => {
  const params: Record<string, any> = {examId}
  const config = typeof options === 'object' && options ? options : {}

  if (typeof config.pageNumber === 'number') {
    params.pageNumber = config.pageNumber
  }

  if (typeof config.pageSize === 'number') {
    params.pageSize = config.pageSize
  }

  if (typeof config.nameFilter === 'string' && config.nameFilter.trim().length > 0) {
    params.nameFilter = config.nameFilter.trim()
  }

  if (typeof config.numberFilter === 'string' && config.numberFilter.trim().length > 0) {
    params.numberFilter = config.numberFilter.trim()
  }

  if (typeof config.sortField === 'string' && config.sortField.trim().length > 0) {
    params.sortField = config.sortField.trim()
  }

  if (typeof config.sortDirection === 'string' && config.sortDirection.trim().length > 0) {
    params.sortDirection = config.sortDirection.trim()
  }

  return axios
    .get(EXAM_RESULT_STUDENTS_URL, {params})
    .then((response: AxiosResponse<any>) => response.data)
}

const getExamResultStudentDetails = (
  examId: number,
  studentSemesterCourseId: number
): Promise<any> => {
  return axios
    .get(EXAM_RESULT_STUDENT_DETAILS_URL, {params: {examId, studentSemesterCourseId}})
    .then((response: AxiosResponse<any>) => response.data)
}

const evaluateStudentBookletCodes = (
  examId: number,
  studentSemesterCourseId: number
): Promise<any> => {
  return axios
    .get(EXAM_RESULT_STUDENT_BOOKLET_EVALUATION_URL, {params: {examId, studentSemesterCourseId}})
    .then((response: AxiosResponse<any>) => response.data)
}

const applyStudentBookletEvaluation = (
  examId: number,
  studentSemesterCourseId: number,
  bookletCode?: string | null
): Promise<any> => {
  const payload: {examId: number; studentSemesterCourseId: number; bookletCode?: string} = {
    examId,
    studentSemesterCourseId,
  }

  if (typeof bookletCode === 'string') {
    const trimmed = bookletCode.trim()
    if (trimmed.length > 0) {
      payload.bookletCode = trimmed
    }
  }

  return axios
    .post(APPLY_STUDENT_BOOKLET_EVALUATION_URL, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

const updateExamAnswerKey = (payload: ExamAnswerKeyUpdatePayload): Promise<any> => {
  return axios
    .post(UPDATE_EXAM_ANSWER_KEY_URL, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

const sendExamGradesToObs = (examId: number, targetCode?: string | null): Promise<any> => {
  const payload: {examId: number; targetCode?: string} = {examId}

  if (typeof targetCode === 'string') {
    const trimmed = targetCode.trim()
    if (trimmed.length > 0) {
      payload.targetCode = trimmed
    }
  }

  return axios
    .post(SEND_EXAM_GRADES_TO_OBS_URL, payload)
    .then((response: AxiosResponse<any>) => response.data)
}

export {
  createCustomCourseSchedule,
  getCustomCourseSchedule,
  deleteCustomCourseSchedule,
  assignSemesterCourseInstructor,
  fetchUserSurveyApplications,
  fetchUserSurveyInfo,
  fetchStudentPostponementInfo,
  submitStudentPostponement,
  getSurveyApplicationDetail,
  submitSurveyApplication,
  fetchGraduateProfile,
  saveGraduateProfile,
  submitGraduateApplication,
  uploadGraduateCv,
  downloadGraduateCv,
  downloadGraduateApplicationsExcel,
  uploadGraduatePhoto,
  fetchGraduateApplications,
  fetchApprovedGraduates,
  approveGraduateApplication,
  rejectGraduateApplication,
  getExamResultStatistics,
  getExamResultQuestions,
  getExamResultInput,
  getExamResultStudents,
  getExamResultStudentDetails,
  evaluateStudentBookletCodes,
  applyStudentBookletEvaluation,
  updateExamAnswerKey,
  sendExamGradesToObs,
  searchApplicationUsers,
}
